import {
  List, Datagrid, TextField, EmailField, DateField, NumberField, EditButton,
  Edit, SimpleForm, TextInput, SelectInput, DateInput, ExportButton, TopToolbar,
  FilterButton, useRecordContext,
} from 'react-admin';
import { Stack, Box, Chip, Typography } from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';

const STATUSES = [
  { id: 'new', name: 'New' },
  { id: 'verified', name: 'Verified' },
  { id: 'archived', name: 'Archived' },
];

const LEAVE_REASONS = [
  { id: 'Graduated', name: 'Graduated' },
  { id: 'Transferred Out', name: 'Transferred Out' },
  { id: 'N/A', name: 'N/A' },
];

/**
 * Search: the `q` box matches name / email / student ID / class fields; the
 * extra filters below match a value exactly (date of birth, class left,
 * student ID, year left) — the four things the school searches by.
 */
const filters = [
  <TextInput key="q" source="q" label="Search name, email, student ID, class" alwaysOn sx={{ minWidth: 300 }} />,
  <DateInput key="dob" source="dateOfBirth" label="Date of birth" alwaysOn />,
  <TextInput key="classLeft" source="classLeft" label="Class left" />,
  <TextInput key="studentId" source="studentId" label="Student ID" />,
  <TextInput key="yearLeft" source="yearLeft" label="Year left" />,
  <SelectInput key="leaveReason" source="leaveReason" label="How they left" choices={LEAVE_REASONS} />,
  <SelectInput key="status" source="status" choices={STATUSES} />,
];

const StatusChip = () => {
  const record = useRecordContext();
  const v = record?.status;
  const color = v === 'new' ? 'warning' : v === 'verified' ? 'success' : 'default';
  return <Chip size="small" label={v} color={color} />;
};

const FullName = () => {
  const record = useRecordContext();
  if (!record) return null;
  return <span>{`${record.firstName || ''} ${record.lastName || ''}`.trim()}</span>;
};

const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

const RegistrationList = () => (
  <List
    sort={{ field: 'createdAt', order: 'DESC' }}
    filters={filters}
    actions={<ListActions />}
    perPage={25}
  >
    <Datagrid rowClick="edit" bulkActionButtons={false}>
      <DateField source="createdAt" label="Registered" />
      <Box label="Name"><FullName /></Box>
      <EmailField source="email" />
      <TextField source="phone" />
      <TextField source="studentId" label="Student ID" />
      <NumberField source="yearLeft" label="Year left" />
      <TextField source="classLeft" label="Class left" />
      <Box label="Status"><StatusChip /></Box>
      <EditButton />
    </Datagrid>
  </List>
);

const RegistrationEdit = () => (
  <Edit redirect="list" mutationMode="pessimistic">
    <SimpleForm>
      <Stack spacing={2} sx={{ maxWidth: 760 }}>
        <Typography variant="overline" color="text.secondary">Alumnus</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextInput source="firstName" label="First name" fullWidth disabled />
          <TextInput source="lastName" label="Last name" fullWidth disabled />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextInput source="email" fullWidth disabled />
          <TextInput source="phone" fullWidth disabled />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextInput source="studentId" label="Student ID" fullWidth disabled />
          <TextInput source="dateOfBirth" label="Date of birth" fullWidth disabled />
        </Stack>

        <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>Present address</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextInput source="addressRoad" label="Road" fullWidth disabled />
          <TextInput source="addressArea" label="Area" fullWidth disabled />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextInput source="addressState" label="State / Division" fullWidth disabled />
          <TextInput source="addressCountry" label="Country" fullWidth disabled />
        </Stack>

        <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>Time at MLIS</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextInput source="joiningYear" label="Joining year" fullWidth disabled />
          <TextInput source="joiningClass" label="Joining class" fullWidth disabled />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextInput source="yearLeft" label="Year left" fullWidth disabled />
          <TextInput source="classLeft" label="Class left" fullWidth disabled />
        </Stack>
        <TextInput source="leaveReason" label="How they left MLIS" fullWidth disabled />

        <Typography variant="overline" color="text.secondary" sx={{ pt: 1 }}>What they've been up to</Typography>
        <TextInput source="message" label="" multiline minRows={4} fullWidth disabled />

        <SelectInput source="status" choices={STATUSES} label="Update status" />
      </Stack>
    </SimpleForm>
  </Edit>
);

export default {
  list: RegistrationList,
  edit: RegistrationEdit,
  icon: SchoolIcon,
  options: { label: 'Alumni registrations' },
  recordRepresentation: (r) => `${r.firstName} ${r.lastName}`,
};
